<script setup lang="ts">
import CampaignUpsell from './components/CampaignUpsell.vue'
interface Country {
  [key: string]: any
}

interface RecipientsFilters {
  choices?: number[]
  surveyQuestionChoices?: number[]
  tags: number[]
  [key: string]: any
}

interface Survey {
  id: number
  choices: Array<{
    id: number
    text: string
  }>
  [key: string]: any
}

interface Tag {
  id: number
  name: string
  [key: string]: any
}

const props = defineProps({
  campaignName: {
    type: String,
    required: true
  },
  countries: {
    type: Object as PropType<Country>,
    required: true
  },
  recipientsFilters: {
    type: Object as PropType<RecipientsFilters>,
    required: true
  },
  optinTargets: {
    type: Number,
    required: true
  },
  message: {
    type: String,
    required: true
  },
  step: {
    type: Number,
    required: true
  },
  surveys: {
    type: [Array, Boolean] as PropType<Survey[] | false>,
    required: true
  },
  tags: {
    type: Array as PropType<Tag[]>,
    required: true
  },
  canProceedToValidationStep: {
    type: Boolean,
    required: true
  },
  isLoadingEstimation: {
    type: Boolean,
    default: false
  }
})

defineEmits<{
  continue: []
}>()

const route = useRoute()

const campaignType = computed(() => route.query.type || 'sms')

const displayableKeys = ['ages', 'points', 'genders', 'stores', 'lists', 'choices', 'tags', 'lastVisit', 'firstCheckin']

const activeFilters = computed(() => {
  const filters = { ...props.recipientsFilters }
  if (props.surveys === false) {
    delete filters.choices
  }

  return Object.fromEntries(
    Object.entries(filters).filter(([key, value]) => {
      if (!displayableKeys.includes(key)) return false
      if (Array.isArray(value) && value.length === 0) return false
      if (key === 'points' && Array.isArray(value) && value.some((v) => v === null)) return false
      if (key === 'lastVisit' || key === 'firstCheckin') {
        return value?.from || value?.to
      }
      return true
    })
  )
})

const choiceNames = computed(() => {
  if (!props.recipientsFilters.choices || props.recipientsFilters.choices.length === 0) {
    return 'Tous les sondages'
  }

  const choiceNames: string[] = []

  if (props.surveys !== false && props.recipientsFilters.surveyQuestionChoices) {
    props.recipientsFilters.surveyQuestionChoices.forEach((choiceId) => {
      props.surveys.forEach((survey) => {
        survey.choices.forEach((choice) => {
          if (choice.id == choiceId) {
            choiceNames.push(choice.text)
          }
        })
      })
    })
  }

  return choiceNames.join(' - ')
})

const tagNames = computed(() => {
  if (props.recipientsFilters.tags.length === 0) {
    return 'Tous les tags'
  }

  return props.recipientsFilters.tags
    .map((tagId) => props.tags.find((t) => t.id == tagId))
    .filter(Boolean)
    .map((tag) => tag?.name)
    .join(' - ')
})
</script>

<template>
  <CampaignUpsell v-if="campaignType === 'sms'" />

  <div class="overflow-hidden rounded-md border bg-white">
    <div class="border-b px-4 py-5 sm:px-6">
      <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
        <div class="mt-2">
          <h3 class="text-lg font-medium leading-6 text-foreground">
            {{ $t(`pages.campaign.campaignCreatePage.${campaignType}.summary.title`) }}
          </h3>
        </div>
        <div v-if="step != 3" class="z-10 ml-4 mt-2 shrink-0">
          <Button
            variant="default-primary"
            class="fixed bottom-0 left-0 w-screen rounded-none py-4 sm:static sm:w-auto sm:rounded-control sm:py-2"
            :disabled="
              (step === 1 && optinTargets > 0 && campaignName?.length > 0) || (step === 2 && canProceedToValidationStep)
                ? false
                : true
            "
            @click="$emit('continue')"
            >{{ $t('pages.campaign.campaignCreatePage.buttons.continue') }}
          </Button>
        </div>
      </div>
    </div>
    <dl class="p-6">
      <div class="flex items-center">
        <img class="w-20" src="/images/campaign/target.svg" alt="target" />
        <div class="ml-4">
          <dt class="mb-2 text-sm font-medium text-muted-foreground">
            <span v-if="isLoadingEstimation">
              <AppSkeleton type="default" />
            </span>
            <span v-else>
              {{
                $t('pages.campaign.campaignCreatePage.summary.recipients', { optinTargets: optinTargets }, optinTargets)
              }}
            </span>
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            <span
              v-for="(filter, key) in activeFilters"
              :key="key"
              class="inline-flex items-center rounded-sm py-0.5 text-xs font-medium text-foreground"
              :class="{ 'mb-2 mr-2 bg-muted px-2': key !== 'stores' && key !== 'lists' }"
            >
              <span v-if="key === 'ages'">
                <span v-if="filter[0] === filter[1]">
                  {{ $t('global.persona.age', { age: filter[0] }) }}
                </span>
                <span v-else>
                  {{ $t('pages.campaign.campaignTargetBlock.agesFromTo', { age1: filter[0], age2: filter[1] }) }}
                </span>
              </span>
              <span v-if="key === 'points'">
                <span v-if="filter[0] === filter[1]">
                  {{ $t('global.persona.points', { points: filter[0] }) }}
                </span>
                <span v-else>
                  {{
                    $t('pages.campaign.campaignTargetBlock.pointsFromTo', { points1: filter[0], points2: filter[1] })
                  }}
                </span>
              </span>
              <span v-else-if="key === 'genders'">
                {{ filter.map((f) => $t('global.gender.' + f)).join(' - ') }}
              </span>
              <div v-else-if="key === 'stores'" class="flex max-h-20 flex-wrap overflow-y-auto">
                <span
                  v-for="store in recipientsFilters.stores"
                  :key="store"
                  class="mb-2 mr-2 rounded-sm bg-muted px-2 py-0.5"
                >
                  {{ store.label }}
                </span>
              </div>
              <div v-else-if="key === 'lists'" class="flex max-h-20 flex-wrap overflow-y-auto">
                <span
                  v-for="list in recipientsFilters.lists"
                  :key="list"
                  class="mb-2 mr-2 rounded-sm bg-muted px-2 py-0.5"
                  >{{ list.name }}</span
                >
              </div>
              <span v-else-if="key === 'choices'">
                {{ choiceNames }}
              </span>
              <span v-else-if="key === 'tags'">
                {{ tagNames }}
              </span>
              <span v-else-if="key === 'lastVisit'">
                {{
                  filter.from
                    ? $t('pages.campaign.campaignTargetBlock.lastVisitFromTo', {
                        lastVisit1: filter.from,
                        lastVisit2: filter.to
                      })
                    : $t('pages.campaign.campaignTargetBlock.allVisits')
                }}
              </span>
              <span v-else-if="key === 'firstCheckin'">
                {{
                  filter.from
                    ? $t('pages.campaign.campaignTargetBlock.firstCheckinFromTo', {
                        firstCheckin1: filter.from,
                        firstCheckin2: filter.to
                      })
                    : $t('pages.campaign.campaignTargetBlock.allCheckins')
                }}
              </span>
            </span>
          </dd>
        </div>
      </div>
    </dl>
  </div>
</template>
